import { ablyChannelNamesForSession } from "_shared/_stores/realtime/ably-channel-names";
import reduxAblyMiddleware, { withCallbacks } from "_shared/_stores/realtime/redux-ably-middleware";
import { permissionChangeTags } from "permissions/_api/permission-cache-tags";
import { permissionListTag, permissionsApi } from "permissions/_api/permissions-api-service";
import { z } from "zod";

/**
 * The `permissions-changed` event: the grants one commit changed in the organization, by id alone.
 * Strings rather than `z.uuid()`, which refuses ids the API accepts.
 */
const PermissionsChangedZ = z.object({
  changes: z.array(
    z.object({
      granteeUserId: z.string().min(1),
      resourceType: z.string().min(1),
      targetId: z.string().min(1).nullish(),
    }),
  ),
});

/**
 * The tags one `permissions-changed` event invalidates, each once, or none for a payload that will
 * not parse: a refetch of nothing rather than of everything.
 */
export function tagsForPermissionsChanged(payload: unknown) {
  const parsed = PermissionsChangedZ.safeParse(payload);
  if (!parsed.success) return [];

  const tags = new Map<string, ReturnType<typeof permissionChangeTags>[number]>();
  for (const change of parsed.data.changes) {
    for (const tag of permissionChangeTags(change)) tags.set(tag.id, tag);
  }
  return [...tags.values()];
}

/**
 * Keeps an administrator's open permissions pages current when another administrator grants,
 * removes or changes a grant (issue #6785).
 *
 * - It invalidates only the person, record, kind and home reads the changed grants can appear in
 *   (`permission-cache-tags.ts`), never `Permission LIST`, so one removal elsewhere does not
 *   refetch every permissions read the tab holds.
 * - The event carries ids, never names or levels; the refetch is what reads the grant, under the
 *   reader's own permissions.
 * - Whether a browser may attach is decided by the Ably token the API mints: `AblyAuthController`
 *   puts the organization's permissions channel in a user's capabilities only while they hold
 *   Member Permissions. The session carries the same answer as `hasMemberPermissionsAccess`, and
 *   `ablyChannelNamesForSession` names no channel without it, so a member who would be refused
 *   never asks. That is noise avoidance, not the gate.
 * - Like the entity notifications feed, it registers no connection-health entry and does not start
 *   the shared connection: the pages still refetch on focus without it.
 * - When Ably reattaches without continuity, the changes it missed cannot be named, so every
 *   permissions read is refetched once (`Permission LIST`, which they all carry).
 */
const callbacks = withCallbacks().add("permissions-changed", (payload: unknown) => (dispatch) => {
  const tags = tagsForPermissionsChanged(payload);
  if (tags.length === 0) return;
  dispatch(permissionsApi.util.invalidateTags(tags));
});

export const permissionsAblyReduxMiddleware = reduxAblyMiddleware({
  callbacks,
  channelName: ablyChannelNamesForSession.entityPermissions,
  onContinuityLost: (dispatch) => {
    dispatch(permissionsApi.util.invalidateTags([permissionListTag]));
  },
  shouldConnectionStartImmediately: false,
});
